/*
 * Home-page copy for the languages without their own block in siteContent.js.
 * Merged on top of the English defaults, so anything missing here falls back to English.
 * Header (top bar, navigation) and footer are seeded in the CMS (backend/scripts/seed_site_chrome.py).
 * Wrap words in *asterisks* to render them in the brand green.
 */

const fr = {
  // header + footer: same text as backend/scripts/seed_site_chrome.py, so the CMS values cause no layout shift
  "topbar": {
    "items": [
      "Aucun frais d'installation",
      "Résiliable à tout moment",
      "Conforme au RGPD"
    ],
    "faq_label": "FAQ"
  },
  "nav": {
    "links": [
      {
        "label": "Accueil",
        "to": "/"
      },
      {
        "label": "Fonctionnalités",
        "to": "/functionaliteiten"
      },
      {
        "label": "Comment ça marche",
        "to": "/hoe-het-werkt"
      },
      {
        "label": "Tarifs",
        "to": "/pricing"
      },
      {
        "label": "À propos",
        "to": "/over-ons"
      },
      {
        "label": "Contact",
        "to": "/contact"
      }
    ],
    "login_label": "Connexion",
    "cta_label": "Essai gratuit",
    "dashboard_label": "Vers le tableau de bord"
  },
  "footer": {
    "description": "Le centre de commande financier des marchands WooCommerce. Bénéfices, coûts et croissance en temps réel, dans un tableau de bord clair.",
    "location": "Flandre-Occidentale, Belgique · TVA BE0793944703",
    "columns": [
      {
        "title": "Produit",
        "links": [
          {
            "label": "Accueil",
            "to": "/"
          },
          {
            "label": "Fonctionnalités",
            "to": "/functionaliteiten"
          },
          {
            "label": "Comment ça marche",
            "to": "/hoe-het-werkt"
          },
          {
            "label": "Tarifs",
            "to": "/pricing"
          }
        ]
      },
      {
        "title": "Entreprise",
        "links": [
          {
            "label": "À propos",
            "to": "/over-ons"
          },
          {
            "label": "Contact",
            "to": "/contact"
          },
          {
            "label": "info@wowprofit.io",
            "to": "mailto:info@wowprofit.io"
          },
          {
            "label": "FAQ",
            "to": "/faq"
          }
        ]
      },
      {
        "title": "Mentions légales",
        "links": [
          {
            "label": "Politique de confidentialité",
            "to": "/privacy-beleid"
          },
          {
            "label": "Conditions d'utilisation",
            "to": "/algemene-voorwaarden"
          },
          {
            "label": "Cookies",
            "to": "/cookies"
          },
          {
            "label": "Plan du site",
            "to": "sitemap"
          }
        ]
      }
    ],
    "copyright": "© {year} Webtron BV — WowProfit.io. Tous droits réservés."
  },
  hero: {
    kicker: 'Essai gratuit de 30 jours',
    title: '*Mesurez votre bénéfice* par boutique WooCommerce',
    subtitle: 'Connectez votre boutique WooCommerce et Google Ads en 2 minutes et voyez immédiatement où votre boutique gagne vraiment de l’argent.',
    cta_primary: 'Essai gratuit',
    cta_secondary: 'Voir la démo',
    stats: [
      { value: '2 400*+*', label: 'Commandes synchronisées par jour' },
      { value: '*<* 10 min', label: 'Temps de configuration moyen' },
      { value: '99,9*%*', label: 'Disponibilité de la plateforme' },
      { value: 'UE', label: 'Hébergé en UE, conforme au RGPD' },
    ],
  },
  intro: { title: '*Comprenez* votre vrai bénéfice' },
  blocks: [
    {
      kicker: 'Le problème',
      title: 'WooCommerce affiche votre chiffre d’affaires. Pas votre bénéfice.',
      body: 'Entre les prix d’achat, les frais de livraison, les budgets publicitaires et les remboursements, la réponse à « est-ce que je gagne vraiment de l’argent ? » se perd vite dans des tableurs et des onglets éparpillés.\nWowProfit.io rassemble automatiquement tous ces chiffres en un seul résultat clair : votre bénéfice net réel, par produit, par pays et par mois.',
      cta_label: 'Essai gratuit',
    },
    {
      kicker: 'Ventes',
      title: 'Chaque commande, avec sa marge',
      body: 'Voyez pour chaque commande ce qui rentre et ce qu’il vous reste — frais de livraison, frais du prestataire de paiement et attribution publicitaire inclus. Fini les calculs manuels.',
      cta_label: 'Essai gratuit',
    },
    {
      kicker: 'Comment ça marche',
      title: 'De la connexion de la boutique au bénéfice en 4 étapes',
      body: 'Connectez WowProfit à votre boutique, saisissez vos coûts et obtenez une vision claire de votre bénéfice réel — au même endroit.',
      cta_label: 'Découvrir WowProfit',
    },
  ],
  features: {
    kicker: 'Fonctionnalités',
    title: 'Tout ce qu’il faut pour optimiser vos bénéfices',
    body: 'Des rapports automatiques aux analyses par IA. WowProfit.io vous aide à suivre chaque euro.',
    cta_label: 'Toutes les fonctionnalités',
    items: [
      { title: 'Rapports automatiques', description: 'Bénéfices quotidiens, mensuels et annuels. Export vers Google Sheets ou CSV.' },
      { title: 'Analyse par pays', description: 'Voyez chiffre d’affaires, TVA et bénéfice net par pays pour savoir quels marchés sont vraiment rentables.' },
      { title: 'Suivi des prix d’achat', description: 'Enregistrez les prix d’achat par produit et voyez instantanément votre marge par produit et par commande.' },
      { title: 'Analyses par IA', description: 'Notre IA signale les marges et les produits en baisse avant qu’ils n’affectent votre chiffre d’affaires.' },
      { title: 'Collaboration d’équipe', description: 'Travaillez avec votre équipe et votre comptable dans un seul tableau de bord partagé.' },
    ],
  },
  radar: {
    kicker: 'Radar des prix',
    title: 'Des *recommandations* claires, sans fouiller le tableau de bord',
    body: 'Notre IA détecte les baisses de marge, les produits en déclin et les écarts de prix avec vos concurrents avant qu’ils n’affectent votre chiffre d’affaires.\nÉvolution des prix par concurrent, mesurée à chaque contrôle quotidien.',
  },
  audience: {
    kicker: 'Pour qui',
    title: '*Conçu* pour toutes les tailles de boutique',
    items: [
      { title: 'Entrepreneurs *indépendants*', text: 'Une boutique, une personne, pas de temps pour les tableurs. Voyez en 10 secondes si vous êtes rentable ce mois-ci.' },
      { title: 'Commerçants *multi-boutiques*', text: 'Gérez plusieurs boutiques WooCommerce depuis un seul tableau de bord et comparez instantanément leur rentabilité.' },
      { title: 'Marketeurs *performance*', text: 'Reliez les dépenses publicitaires au vrai résultat net plutôt qu’au seul chiffre d’affaires ou ROAS.' },
    ],
  },
  pricing: {
    kicker: 'Tarifs',
    title: 'Des tarifs *transparents*, sans frais cachés',
    note: 'Tous les prix s’entendent hors TVA. Commencez par un essai gratuit de 30 jours.',
    monthly_label: 'Mensuel',
    yearly_label: 'Annuel',
    per_month: 'par mois',
    per_year: 'par an',
    all_plans_label: 'Voir tous les tarifs',
  },
  cta: {
    title: 'Sachez dès aujourd’hui ce que vous gagnez vraiment',
    subtitle: 'Essayez gratuitement, sans carte bancaire.',
    primary_label: 'Essai gratuit',
    secondary_label: 'Parler à l’équipe',
  },
};

const de = {
  // header + footer: same text as backend/scripts/seed_site_chrome.py, so the CMS values cause no layout shift
  "topbar": {
    "items": [
      "Keine Einrichtungskosten",
      "Jederzeit kündbar",
      "DSGVO-konform"
    ],
    "faq_label": "FAQ"
  },
  "nav": {
    "links": [
      {
        "label": "Startseite",
        "to": "/"
      },
      {
        "label": "Funktionen",
        "to": "/functionaliteiten"
      },
      {
        "label": "So funktioniert's",
        "to": "/hoe-het-werkt"
      },
      {
        "label": "Preise",
        "to": "/pricing"
      },
      {
        "label": "Über uns",
        "to": "/over-ons"
      },
      {
        "label": "Kontakt",
        "to": "/contact"
      }
    ],
    "login_label": "Anmelden",
    "cta_label": "Kostenlos starten",
    "dashboard_label": "Zum Dashboard"
  },
  "footer": {
    "description": "Die Finanzzentrale für WooCommerce-Händler. Gewinn, Kosten und Wachstum in Echtzeit, in einem übersichtlichen Dashboard.",
    "location": "Westflandern, Belgien · USt-IdNr. BE0793944703",
    "columns": [
      {
        "title": "Produkt",
        "links": [
          {
            "label": "Startseite",
            "to": "/"
          },
          {
            "label": "Funktionen",
            "to": "/functionaliteiten"
          },
          {
            "label": "So funktioniert's",
            "to": "/hoe-het-werkt"
          },
          {
            "label": "Preise",
            "to": "/pricing"
          }
        ]
      },
      {
        "title": "Unternehmen",
        "links": [
          {
            "label": "Über uns",
            "to": "/over-ons"
          },
          {
            "label": "Kontakt",
            "to": "/contact"
          },
          {
            "label": "info@wowprofit.io",
            "to": "mailto:info@wowprofit.io"
          },
          {
            "label": "FAQ",
            "to": "/faq"
          }
        ]
      },
      {
        "title": "Rechtliches",
        "links": [
          {
            "label": "Datenschutzerklärung",
            "to": "/privacy-beleid"
          },
          {
            "label": "Allgemeine Geschäftsbedingungen",
            "to": "/algemene-voorwaarden"
          },
          {
            "label": "Cookies",
            "to": "/cookies"
          },
          {
            "label": "Sitemap",
            "to": "sitemap"
          }
        ]
      }
    ],
    "copyright": "© {year} Webtron BV — WowProfit.io. Alle Rechte vorbehalten."
  },
  hero: {
    kicker: '30 Tage kostenlos testen',
    title: '*Miss deinen Gewinn* pro WooCommerce-Shop',
    subtitle: 'Verbinde deinen WooCommerce-Shop und Google Ads in 2 Minuten und sieh sofort, womit dein Shop wirklich Geld verdient.',
    cta_primary: 'Kostenlos starten',
    cta_secondary: 'Demo ansehen',
    stats: [
      { value: '2.400*+*', label: 'Synchronisierte Bestellungen pro Tag' },
      { value: '*<* 10 Min.', label: 'Durchschnittliche Einrichtungszeit' },
      { value: '99,9*%*', label: 'Verfügbarkeit der Plattform' },
      { value: 'EU', label: 'In der EU gehostet, DSGVO-konform' },
    ],
  },
  intro: { title: '*Verstehe* deinen echten Gewinn' },
  blocks: [
    {
      kicker: 'Das Problem',
      title: 'WooCommerce zeigt deinen Umsatz. Nicht deinen Gewinn.',
      body: 'Zwischen Einkaufspreisen, Versandkosten, Werbebudgets und Retouren geht die Antwort auf „Verdiene ich hier wirklich Geld?“ schnell in verstreuten Tabellen und Tabs verloren.\nWowProfit.io führt all diese Zahlen automatisch zu einer klaren Kennzahl zusammen: deinem echten Nettogewinn, pro Produkt, pro Land und pro Monat.',
      cta_label: 'Kostenlos starten',
    },
    {
      kicker: 'Verkäufe',
      title: 'Jede Bestellung, mit ihrer Marge',
      body: 'Sieh bei jeder Bestellung, was hereinkommt und was dir bleibt — inklusive Versandkosten, Zahlungsgebühren und Werbezuordnung. Schluss mit manuellem Rechnen.',
      cta_label: 'Kostenlos starten',
    },
    {
      kicker: 'So funktioniert es',
      title: 'Von der Shop-Anbindung zum Gewinn in 4 Schritten',
      body: 'Verbinde WowProfit mit deinem Shop, trage deine Kosten ein und erhalte einen klaren Blick auf deinen echten Gewinn — alles an einem Ort.',
      cta_label: 'WowProfit entdecken',
    },
  ],
  features: {
    kicker: 'Funktionen',
    title: 'Alles, was du für mehr Gewinn brauchst',
    body: 'Von automatischen Berichten bis zu KI-gestützten Einblicken. WowProfit.io hilft dir, jeden Euro im Blick zu behalten.',
    cta_label: 'Alle Funktionen ansehen',
    items: [
      { title: 'Automatische Berichte', description: 'Tägliche, monatliche und jährliche Gewinnberechnungen. Export nach Google Sheets oder CSV.' },
      { title: 'Analyse pro Land', description: 'Sieh Umsatz, MwSt. und Nettogewinn pro Land und erkenne, welche Märkte sich wirklich lohnen.' },
      { title: 'Einkaufspreis-Tracking', description: 'Erfasse Einkaufspreise pro Produkt und sieh sofort deine Marge pro Produkt und Bestellung.' },
      { title: 'KI-gestützte Einblicke', description: 'Unsere KI meldet sinkende Margen und Produkte, bevor sie deinen Umsatz treffen.' },
      { title: 'Zusammenarbeit im Team', description: 'Arbeite mit deinem Team und deinem Steuerberater in einem gemeinsamen Dashboard.' },
    ],
  },
  radar: {
    kicker: 'Preisradar',
    title: 'Klare *Empfehlungen*, ohne im Dashboard zu suchen',
    body: 'Unsere KI erkennt sinkende Margen, rückläufige Produkte und Preisunterschiede zu Wettbewerbern, bevor sie deinen Umsatz beeinflussen.\nPreisentwicklung pro Wettbewerber, bei jeder täglichen Prüfung gemessen.',
  },
  audience: {
    kicker: 'Für wen',
    title: '*Gebaut* für Webshops jeder Größe',
    items: [
      { title: '*Solo*-Unternehmer', text: 'Ein Shop, eine Person, keine Zeit für Tabellen. Sieh in 10 Sekunden, ob du diesen Monat profitabel bist.' },
      { title: '*Multi-Shop*-Händler', text: 'Verwalte mehrere WooCommerce-Shops in einem Dashboard und vergleiche sofort ihre Profitabilität.' },
      { title: '*Performance*-Marketer', text: 'Verknüpfe Werbeausgaben mit dem echten Nettoergebnis statt nur mit Umsatz oder ROAS.' },
    ],
  },
  pricing: {
    kicker: 'Preise',
    title: '*Transparente* Preise, keine versteckten Kosten',
    note: 'Alle Preise exklusive MwSt. Starte mit 30 Tagen kostenlos.',
    monthly_label: 'Monatlich',
    yearly_label: 'Jährlich',
    per_month: 'pro Monat',
    per_year: 'pro Jahr',
    all_plans_label: 'Alle Tarife ansehen',
  },
  cta: {
    title: 'Wisse heute, was du wirklich verdienst',
    subtitle: 'Kostenlos testen, keine Kreditkarte nötig.',
    primary_label: 'Kostenlos starten',
    secondary_label: 'Mit dem Team sprechen',
  },
};

const es = {
  // header + footer: same text as backend/scripts/seed_site_chrome.py, so the CMS values cause no layout shift
  "topbar": {
    "items": [
      "Sin costes de configuración",
      "Cancela cuando quieras",
      "Conforme al RGPD"
    ],
    "faq_label": "FAQ"
  },
  "nav": {
    "links": [
      {
        "label": "Inicio",
        "to": "/"
      },
      {
        "label": "Funciones",
        "to": "/functionaliteiten"
      },
      {
        "label": "Cómo funciona",
        "to": "/hoe-het-werkt"
      },
      {
        "label": "Precios",
        "to": "/pricing"
      },
      {
        "label": "Sobre nosotros",
        "to": "/over-ons"
      },
      {
        "label": "Contacto",
        "to": "/contact"
      }
    ],
    "login_label": "Iniciar sesión",
    "cta_label": "Empieza gratis",
    "dashboard_label": "Ir al panel"
  },
  "footer": {
    "description": "El centro de mando financiero para comerciantes de WooCommerce. Beneficios, costes y crecimiento en tiempo real, en un panel claro.",
    "location": "Flandes Occidental, Bélgica · IVA BE0793944703",
    "columns": [
      {
        "title": "Producto",
        "links": [
          {
            "label": "Inicio",
            "to": "/"
          },
          {
            "label": "Funciones",
            "to": "/functionaliteiten"
          },
          {
            "label": "Cómo funciona",
            "to": "/hoe-het-werkt"
          },
          {
            "label": "Precios",
            "to": "/pricing"
          }
        ]
      },
      {
        "title": "Empresa",
        "links": [
          {
            "label": "Sobre nosotros",
            "to": "/over-ons"
          },
          {
            "label": "Contacto",
            "to": "/contact"
          },
          {
            "label": "info@wowprofit.io",
            "to": "mailto:info@wowprofit.io"
          },
          {
            "label": "FAQ",
            "to": "/faq"
          }
        ]
      },
      {
        "title": "Legal",
        "links": [
          {
            "label": "Política de privacidad",
            "to": "/privacy-beleid"
          },
          {
            "label": "Términos del servicio",
            "to": "/algemene-voorwaarden"
          },
          {
            "label": "Cookies",
            "to": "/cookies"
          },
          {
            "label": "Mapa del sitio",
            "to": "sitemap"
          }
        ]
      }
    ],
    "copyright": "© {year} Webtron BV — WowProfit.io. Todos los derechos reservados."
  },
  hero: {
    kicker: 'Prueba gratis durante 30 días',
    title: '*Mide tu beneficio* por cada tienda WooCommerce',
    subtitle: 'Conecta tu tienda WooCommerce y Google Ads en 2 minutos y descubre al instante dónde gana dinero de verdad tu tienda.',
    cta_primary: 'Empieza gratis',
    cta_secondary: 'Ver demo',
    stats: [
      { value: '2.400*+*', label: 'Pedidos sincronizados al día' },
      { value: '*<* 10 min', label: 'Tiempo medio de configuración' },
      { value: '99,9*%*', label: 'Disponibilidad de la plataforma' },
      { value: 'UE', label: 'Alojado en la UE, conforme al RGPD' },
    ],
  },
  intro: { title: '*Entiende* tu beneficio real' },
  blocks: [
    {
      kicker: 'El problema',
      title: 'WooCommerce muestra tus ingresos. No tu beneficio.',
      body: 'Entre precios de compra, gastos de envío, presupuestos publicitarios y devoluciones, la respuesta a «¿de verdad gano dinero con esto?» se pierde enseguida entre hojas de cálculo y pestañas.\nWowProfit.io reúne automáticamente todas esas cifras en un solo número claro: tu beneficio neto real, por producto, por país y por mes.',
      cta_label: 'Empieza gratis',
    },
    {
      kicker: 'Ventas',
      title: 'Cada pedido, con su margen',
      body: 'Consulta en cada pedido lo que entra y lo que te queda — incluidos envío, comisiones del proveedor de pago y atribución publicitaria. Se acabaron los cálculos manuales.',
      cta_label: 'Empieza gratis',
    },
    {
      kicker: 'Cómo funciona',
      title: 'De la conexión de la tienda al beneficio en 4 pasos',
      body: 'Conecta WowProfit a tu tienda, introduce tus costes y obtén una visión clara de tu beneficio real, todo en un mismo lugar.',
      cta_label: 'Descubre WowProfit',
    },
  ],
  features: {
    kicker: 'Funciones',
    title: 'Todo lo que necesitas para optimizar el beneficio',
    body: 'Desde informes automáticos hasta análisis con IA. WowProfit.io te ayuda a controlar cada euro.',
    cta_label: 'Ver todas las funciones',
    items: [
      { title: 'Informes automáticos', description: 'Cálculo de beneficios diario, mensual y anual. Exporta a Google Sheets o CSV.' },
      { title: 'Análisis por país', description: 'Consulta ingresos, IVA y beneficio neto por país y descubre qué mercados compensan de verdad.' },
      { title: 'Seguimiento de costes', description: 'Registra los precios de compra por producto y consulta al instante tu margen por producto y pedido.' },
      { title: 'Análisis con IA', description: 'Nuestra IA detecta márgenes y productos a la baja antes de que afecten a tus ingresos.' },
      { title: 'Trabajo en equipo', description: 'Trabaja con tu equipo y tu contable en un único panel compartido.' },
    ],
  },
  radar: {
    kicker: 'Radar de precios',
    title: '*Recomendaciones* claras, sin buscar en el panel',
    body: 'Nuestra IA detecta caídas de margen, productos en declive y diferencias de precio con la competencia antes de que afecten a tus ingresos.\nEvolución de precios por competidor, medida en cada comprobación diaria.',
  },
  audience: {
    kicker: 'Para quién',
    title: '*Creado* para tiendas online de todos los tamaños',
    items: [
      { title: 'Emprendedores *autónomos*', text: 'Una tienda, una persona, sin tiempo para hojas de cálculo. Comprueba en 10 segundos si este mes eres rentable.' },
      { title: 'Comercios *multitienda*', text: 'Gestiona varias tiendas WooCommerce desde un solo panel y compara al instante su rentabilidad.' },
      { title: 'Marketers de *rendimiento*', text: 'Vincula la inversión publicitaria con el resultado neto real, no solo con ingresos o ROAS.' },
    ],
  },
  pricing: {
    kicker: 'Precios',
    title: 'Precios *transparentes*, sin costes ocultos',
    note: 'Todos los precios sin IVA. Empieza con 30 días de prueba gratuita.',
    monthly_label: 'Mensual',
    yearly_label: 'Anual',
    per_month: 'al mes',
    per_year: 'al año',
    all_plans_label: 'Ver todos los planes',
  },
  cta: {
    title: 'Descubre hoy lo que ganas de verdad',
    subtitle: 'Pruébalo gratis, sin tarjeta.',
    primary_label: 'Empieza gratis',
    secondary_label: 'Hablar con el equipo',
  },
};

const zh = {
  // header + footer: same text as backend/scripts/seed_site_chrome.py, so the CMS values cause no layout shift
  "topbar": {
    "items": [
      "无需设置费用",
      "随时取消",
      "符合 GDPR"
    ],
    "faq_label": "常见问题"
  },
  "nav": {
    "links": [
      {
        "label": "首页",
        "to": "/"
      },
      {
        "label": "功能",
        "to": "/functionaliteiten"
      },
      {
        "label": "工作原理",
        "to": "/hoe-het-werkt"
      },
      {
        "label": "价格",
        "to": "/pricing"
      },
      {
        "label": "关于我们",
        "to": "/over-ons"
      },
      {
        "label": "联系我们",
        "to": "/contact"
      }
    ],
    "login_label": "登录",
    "cta_label": "免费开始",
    "dashboard_label": "前往仪表盘"
  },
  "footer": {
    "description": "WooCommerce 商家的财务指挥中心。在一个清晰的仪表盘中实时查看利润、成本与增长。",
    "location": "比利时西佛兰德 · 增值税号 BE0793944703",
    "columns": [
      {
        "title": "产品",
        "links": [
          {
            "label": "首页",
            "to": "/"
          },
          {
            "label": "功能",
            "to": "/functionaliteiten"
          },
          {
            "label": "工作原理",
            "to": "/hoe-het-werkt"
          },
          {
            "label": "价格",
            "to": "/pricing"
          }
        ]
      },
      {
        "title": "公司",
        "links": [
          {
            "label": "关于我们",
            "to": "/over-ons"
          },
          {
            "label": "联系我们",
            "to": "/contact"
          },
          {
            "label": "info@wowprofit.io",
            "to": "mailto:info@wowprofit.io"
          },
          {
            "label": "常见问题",
            "to": "/faq"
          }
        ]
      },
      {
        "title": "法律",
        "links": [
          {
            "label": "隐私政策",
            "to": "/privacy-beleid"
          },
          {
            "label": "服务条款",
            "to": "/algemene-voorwaarden"
          },
          {
            "label": "Cookie",
            "to": "/cookies"
          },
          {
            "label": "网站地图",
            "to": "sitemap"
          }
        ]
      }
    ],
    "copyright": "© {year} Webtron BV — WowProfit.io. 保留所有权利。"
  },
  hero: {
    kicker: '30 天免费试用',
    title: '按 WooCommerce 商店*衡量您的利润*',
    subtitle: '2 分钟内连接您的 WooCommerce 商店和 Google Ads，立即看清您的网店真正在哪里赚钱。',
    cta_primary: '免费开始',
    cta_secondary: '查看演示',
    stats: [
      { value: '2,400*+*', label: '每日同步订单' },
      { value: '*<* 10 分钟', label: '平均设置时间' },
      { value: '99.9*%*', label: '平台可用率' },
      { value: 'EU', label: '欧盟托管，符合 GDPR' },
    ],
  },
  intro: { title: '*看清*您的真实利润' },
  blocks: [
    {
      kicker: '问题所在',
      title: 'WooCommerce 显示的是营收，而不是利润。',
      body: '进货价、运费、广告预算和退款交织在一起，“我真的赚钱了吗？”这个问题的答案很容易淹没在零散的表格和标签页里。\nWowProfit.io 会自动把所有数据汇总成一个清晰的数字：您的真实净利润，按产品、按国家、按月份。',
      cta_label: '免费开始',
    },
    {
      kicker: '销售',
      title: '每一笔订单，都带着它的利润率',
      body: '查看每笔订单的收入与您实际留下的部分——包括运费、支付服务商费用和广告归因。告别手工计算。',
      cta_label: '免费开始',
    },
    {
      kicker: '工作原理',
      title: '从连接商店到看清利润，只需 4 步',
      body: '将 WowProfit 连接到您的商店，录入成本，即可在同一个地方清楚看到您的真实利润。',
      cta_label: '了解 WowProfit',
    },
  ],
  features: {
    kicker: '功能',
    title: '优化利润所需的一切',
    body: '从自动报表到 AI 洞察。WowProfit.io 帮您追踪每一欧元。',
    cta_label: '查看全部功能',
    items: [
      { title: '自动报表', description: '按日、月、年自动计算利润。可导出到 Google Sheets 或 CSV。' },
      { title: '分国家分析', description: '按国家查看营收、增值税和净利润，看清哪些市场真正值得投入。' },
      { title: '成本价追踪', description: '按产品记录进货价，立即看到每个产品和每笔订单的利润率。' },
      { title: 'AI 洞察', description: '我们的 AI 会在利润率和产品表现下滑影响营收之前发出提醒。' },
      { title: '团队协作', description: '与团队和会计在同一个共享仪表盘中协作。' },
    ],
  },
  radar: {
    kicker: '价格雷达',
    title: '清晰的*建议*，无需在仪表盘里翻找',
    body: '我们的 AI 会在利润率下降、产品走弱以及与竞争对手的价差影响营收之前发现它们。\n按竞争对手展示价格走势，每次每日检查都会记录。',
  },
  audience: {
    kicker: '适合谁',
    title: '为各种规模的网店*而建*',
    items: [
      { title: '*个人*创业者', text: '一家店、一个人，没时间做表格。10 秒内看清本月是否盈利。' },
      { title: '*多店*零售商', text: '在一个仪表盘中管理多个 WooCommerce 商店，并即时比较它们的盈利能力。' },
      { title: '*效果*营销人员', text: '把广告支出与真实净收益挂钩，而不只是营收或 ROAS。' },
    ],
  },
  pricing: {
    kicker: '价格',
    title: '*透明*定价，没有隐藏费用',
    note: '所有价格均不含增值税。先免费试用 30 天。',
    monthly_label: '按月',
    yearly_label: '按年',
    per_month: '每月',
    per_year: '每年',
    all_plans_label: '查看全部方案',
  },
  cta: {
    title: '今天就知道您到底赚了多少',
    subtitle: '免费试用，无需信用卡。',
    primary_label: '免费开始',
    secondary_label: '联系团队',
  },
};

export const SITE_TRANSLATIONS = { fr, de, es, zh };
